import React, { useEffect, useRef, useState } from 'react';

const DigitalMarketingParticles = () => {
  const canvasRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [activeMetrics, setActiveMetrics] = useState([]);

  // Digital marketing metrics and terms
  const marketingTerms = [
    'SEO', 'ROI', 'CTR', 'CPC', 'CPA', 'CVR', 'KPI', 'PPC', 'SMM', 'CRM',
    'CLICK', 'CONVERT', 'LEAD', 'TRAFFIC', 'ENGAGE', 'GROWTH', 'BRAND', 'ANALYTICS',
    'ADWORDS', 'SOCIAL', 'CONTENT', 'EMAIL', 'INFLUENCER', 'A/B TEST', 'FUNNEL', 'UX'
  ];

  const metricTypes = ['📈', '📊', '📱', '💡', '🚀', '🎯', '💰', '🔍', '📢', '📧'];

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const particles = [];
    const particleCount = Math.min(120, Math.floor(window.innerWidth * window.innerHeight / 12000));

    // Digital marketing color palette
    const colorPalettes = [
      ['#3B82F6', '#1D4ED8', '#2563EB'], // Blue (Facebook, LinkedIn)
      ['#10B981', '#059669', '#047857'], // Green (SEO, Growth)
      ['#F59E0B', '#D97706', '#B45309'], // Amber (Engagement)
      ['#8B5CF6', '#7C3AED', '#6D28D9'], // Purple (Instagram)
      ['#EF4444', '#DC2626', '#B91C1C'], // Red (YouTube, Alerts)
    ];

    const currentPalette = colorPalettes[0];

    class DigitalParticle {
      constructor(type = 'normal') {
        this.type = type;
        this.reset();
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.color = currentPalette[Math.floor(Math.random() * currentPalette.length)];
        
        // For text particles
        if (type === 'text') {
          this.text = marketingTerms[Math.floor(Math.random() * marketingTerms.length)];
          this.size = Math.max(12, Math.min(18, this.text.length * 2));
        } else if (type === 'metric') {
          this.text = metricTypes[Math.floor(Math.random() * metricTypes.length)];
          this.size = 20;
        }
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = this.type === 'normal' ? Math.random() * 3 + 1 : this.size || 15;
        this.speedX = Math.random() * 1 - 0.5;
        this.speedY = Math.random() * 1 - 0.5;
        this.originalSpeedX = this.speedX;
        this.originalSpeedY = this.speedY;
        this.alpha = Math.random() * 0.6 + 0.4;
        this.pulseSpeed = Math.random() * 0.03 + 0.02;
        this.pulseOffset = Math.random() * Math.PI * 2;
        this.pulseAmount = this.type === 'text' ? 0.1 : 0.3;
        this.rotation = Math.random() * Math.PI * 2;
        this.rotationSpeed = (Math.random() - 0.5) * 0.02;
        this.glowIntensity = Math.random() * 0.5 + 0.5;
        this.dataStream = [];
        this.maxStreamLength = 3;
      }

      update(mouseX, mouseY) {
        // Mouse interaction - stronger for metrics
        const dx = mouseX - this.x;
        const dy = mouseY - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const maxDistance = this.type === 'metric' ? 200 : 100;
        
        if (distance < maxDistance && isHovering) {
          const angle = Math.atan2(dy, dx);
          const force = (maxDistance - distance) / maxDistance * (this.type === 'metric' ? 2 : 1);
          this.speedX -= Math.cos(angle) * force * 0.8;
          this.speedY -= Math.sin(angle) * force * 0.8;
          
          // Add data stream when near mouse
          if (Math.random() > 0.95) {
            this.dataStream.push({
              x: this.x,
              y: this.y,
              life: 100
            });
          }
        } else {
          this.speedX += (this.originalSpeedX - this.speedX) * 0.05;
          this.speedY += (this.originalSpeedY - this.speedY) * 0.05;
        }

        this.x += this.speedX;
        this.y += this.speedY;

        // Boundary with bounce
        const bounceFactor = 0.8;
        if (this.x > canvas.width - this.size) {
          this.x = canvas.width - this.size;
          this.speedX *= -bounceFactor;
        }
        if (this.x < this.size) {
          this.x = this.size;
          this.speedX *= -bounceFactor;
        }
        if (this.y > canvas.height - this.size) {
          this.y = canvas.height - this.size;
          this.speedY *= -bounceFactor;
        }
        if (this.y < this.size) {
          this.y = this.size;
          this.speedY *= -bounceFactor;
        }

        // Update data stream
        this.dataStream.forEach(stream => stream.life -= 2);
        this.dataStream = this.dataStream.filter(s => s.life > 0);

        // Rotation for metrics
        if (this.type === 'metric') {
          this.rotation += this.rotationSpeed;
        }

        // Friction
        this.speedX *= 0.98;
        this.speedY *= 0.98;
      }

      draw() {
        const currentTime = Date.now() * 0.001;
        const pulse = Math.sin(currentTime * this.pulseSpeed + this.pulseOffset) * this.pulseAmount + 1;
        const finalSize = this.size * pulse;

        // Draw data stream (digital trail)
        this.dataStream.forEach((stream, i) => {
          const alpha = (stream.life / 100) * 0.3;
          ctx.beginPath();
          ctx.moveTo(this.x, this.y);
          ctx.lineTo(stream.x, stream.y);
          ctx.strokeStyle = `${this.color}${Math.floor(alpha * 255).toString(16).padStart(2, '0')}`;
          ctx.lineWidth = 1;
          ctx.setLineDash([5, 5]);
          ctx.stroke();
          ctx.setLineDash([]);
        });

        // Different drawing based on type
        if (this.type === 'normal') {
          // Regular particle with digital glow
          this.drawDigitalParticle(finalSize);
        } else if (this.type === 'text') {
          // Marketing term text
          this.drawMarketingText(finalSize);
        } else if (this.type === 'metric') {
          // Metric icon/emoji
          this.drawMetricIcon(finalSize);
        }

        // Add binary code effect occasionally
        if (Math.random() > 0.99 && this.type !== 'text') {
          this.drawBinaryEffect();
        }
      }

      drawDigitalParticle(size) {
        // Main particle with digital grid effect
        ctx.beginPath();
        ctx.arc(this.x, this.y, size, 0, Math.PI * 2);
        ctx.fillStyle = `${this.color}${Math.floor(this.alpha * 255).toString(16).padStart(2, '0')}`;
        ctx.fill();

        // Digital grid overlay
        ctx.strokeStyle = `${this.color}80`;
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        for (let i = 0; i < 8; i++) {
          const angle = (i * Math.PI * 2) / 8;
          const x1 = this.x + Math.cos(angle) * size * 0.7;
          const y1 = this.y + Math.sin(angle) * size * 0.7;
          const x2 = this.x + Math.cos(angle + Math.PI / 8) * size * 0.9;
          const y2 = this.y + Math.sin(angle + Math.PI / 8) * size * 0.9;
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
        }
        ctx.stroke();

        // Center dot
        ctx.beginPath();
        ctx.arc(this.x, this.y, size * 0.3, 0, Math.PI * 2);
        ctx.fillStyle = '#FFFFFF';
        ctx.fill();
      }

      drawMarketingText(size) {
        ctx.save();
        ctx.translate(this.x, this.y);
        
        // Background box
        ctx.fillStyle = `${this.color}20`;
        ctx.fillRect(-size, -size/2, size*2, size);
        
        // Text
        ctx.font = `bold ${size}px 'Courier New', monospace`;
        ctx.fillStyle = this.color;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.text, 0, 0);
        
        // Border
        ctx.strokeStyle = `${this.color}80`;
        ctx.lineWidth = 1;
        ctx.strokeRect(-size, -size/2, size*2, size);
        
        ctx.restore();
      }

      drawMetricIcon(size) {
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);
        
        // Background circle
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, size);
        gradient.addColorStop(0, `${this.color}80`);
        gradient.addColorStop(1, `${this.color}20`);
        
        ctx.beginPath();
        ctx.arc(0, 0, size, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
        
        // Border
        ctx.strokeStyle = this.color;
        ctx.lineWidth = 2;
        ctx.stroke();
        
        // Icon/Emoji
        ctx.font = `${size}px Arial`;
        ctx.fillStyle = '#FFFFFF';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.text, 0, 0);
        
        // Pulse effect
        const pulseTime = Date.now() * 0.001;
        const pulseSize = size * (1 + Math.sin(pulseTime * 3) * 0.1);
        
        ctx.beginPath();
        ctx.arc(0, 0, pulseSize, 0, Math.PI * 2);
        ctx.strokeStyle = `${this.color}30`;
        ctx.lineWidth = 1;
        ctx.stroke();
        
        ctx.restore();
      }

      drawBinaryEffect() {
        const binary = ['0', '1'];
        const x = this.x + (Math.random() - 0.5) * this.size * 3;
        const y = this.y + (Math.random() - 0.5) * this.size * 3;
        
        ctx.font = '12px monospace';
        ctx.fillStyle = `${this.color}80`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(binary[Math.floor(Math.random() * 2)], x, y);
      }
    }

    const initParticles = () => {
      particles.length = 0;
      for (let i = 0; i < particleCount; i++) {
        let type = 'normal';
        if (i % 8 === 0) type = 'metric';
        else if (i % 5 === 0) type = 'text';
        
        particles.push(new DigitalParticle(type));
      }
    };

    const drawNetworkConnections = () => {
      const maxDistance = 150;
      
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < maxDistance) {
            const opacity = 0.15 * (1 - distance / maxDistance);
            
            // Style based on particle types
            let lineWidth = 0.5;
            if (particles[i].type === 'metric' || particles[j].type === 'metric') {
              lineWidth = 1;
            }
            
            ctx.strokeStyle = `rgba(100, 100, 255, ${opacity})`;
            ctx.lineWidth = lineWidth;
            ctx.setLineDash([particles[i].type === 'text' ? 10 : 5, 5]);
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
            ctx.setLineDash([]);
          }
        }
      }
    };

    const drawDataFlow = () => {
      // Draw flowing data between particles
      particles.forEach(particle => {
        if (particle.type === 'metric' && Math.random() > 0.98) {
          // Find nearest text particle
          let nearestParticle = null;
          let minDistance = Infinity;
          
          particles.forEach(p => {
            if (p !== particle && p.type === 'text') {
              const dx = p.x - particle.x;
              const dy = p.y - particle.y;
              const distance = Math.sqrt(dx * dx + dy * dy);
              if (distance < minDistance && distance < 300) {
                minDistance = distance;
                nearestParticle = p;
              }
            }
          });
          
          if (nearestParticle) {
            // Draw data flow line
            const gradient = ctx.createLinearGradient(
              particle.x, particle.y,
              nearestParticle.x, nearestParticle.y
            );
            gradient.addColorStop(0, `${particle.color}80`);
            gradient.addColorStop(1, `${nearestParticle.color}80`);
            
            ctx.strokeStyle = gradient;
            ctx.lineWidth = 1;
            ctx.setLineDash([10, 5]);
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(nearestParticle.x, nearestParticle.y);
            ctx.stroke();
            ctx.setLineDash([]);
          }
        }
      });
    };

    const drawAnalyticsDashboard = () => {
      // Draw floating analytics charts
      const time = Date.now() * 0.001;
      
      // Bar chart
      if (Math.sin(time * 0.3) > 0.9) {
        const x = canvas.width * 0.1;
        const y = canvas.height * 0.1;
        const width = 100;
        const height = 60;
        
        ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
        ctx.fillRect(x, y, width, height);
        
        // Bars
        const bars = 5;
        for (let i = 0; i < bars; i++) {
          const barHeight = Math.random() * height * 0.8;
          ctx.fillStyle = i % 2 === 0 ? '#3B82F6' : '#10B981';
          ctx.fillRect(
            x + (i * width / bars) + 2,
            y + height - barHeight,
            width / bars - 4,
            barHeight
          );
        }
        
        // Label
        ctx.font = '10px Arial';
        ctx.fillStyle = '#FFFFFF';
        ctx.fillText('ANALYTICS', x + 5, y + 15);
      }
    };

    const drawMouseEffects = () => {
      if (!isHovering) return;

      const time = Date.now() * 0.001;
      
      // Cursor ring
      ctx.beginPath();
      ctx.arc(mousePos.x, mousePos.y, 25, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(59, 130, 246, ${0.5 + Math.sin(time * 5) * 0.3})`;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Data points around cursor
      for (let i = 0; i < 8; i++) {
        const angle = (i * Math.PI * 2) / 8 + time;
        const radius = 40 + Math.sin(time * 3 + i) * 10;
        const x = mousePos.x + Math.cos(angle) * radius;
        const y = mousePos.y + Math.sin(angle) * radius;
        
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(16, 185, 129, ${0.7})`;
        ctx.fill();
      }
    };

    const animate = () => {
      // Digital gradient background
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, 'rgba(15, 23, 42, 0.95)');
      gradient.addColorStop(1, 'rgba(30, 41, 59, 0.95)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Digital grid
      drawDigitalGrid();

      // Update and draw particles
      particles.forEach(particle => {
        particle.update(mousePos.x, mousePos.y);
        particle.draw();
      });

      // Draw connections
      drawNetworkConnections();
      drawDataFlow();

      // Draw analytics
      drawAnalyticsDashboard();

      // Mouse effects
      drawMouseEffects();

      // Floating digital marketing stats
      drawFloatingStats();

      animationFrameId = requestAnimationFrame(animate);
    };

    const drawDigitalGrid = () => {
      const gridSize = 50;
      ctx.strokeStyle = 'rgba(100, 100, 255, 0.1)';
      ctx.lineWidth = 0.5;

      // Vertical lines
      for (let x = 0; x < canvas.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }

      // Horizontal lines
      for (let y = 0; y < canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }

      // Binary code background effect
      if (Math.random() > 0.7) {
        ctx.font = '12px monospace';
        ctx.fillStyle = 'rgba(0, 255, 0, 0.05)';
        for (let i = 0; i < 10; i++) {
          const x = Math.random() * canvas.width;
          const y = Math.random() * canvas.height;
          const binary = Math.random() > 0.5 ? '101010' : '010101';
          ctx.fillText(binary, x, y);
        }
      }
    };

    const drawFloatingStats = () => {
      const time = Date.now() * 0.001;
      
      // Floating stats like in analytics dashboard
      const stats = [
        { label: 'ROI', value: `${(Math.sin(time) * 50 + 150).toFixed(1)}%`, color: '#10B981' },
        { label: 'CTR', value: `${(Math.cos(time * 0.7) * 2 + 4).toFixed(1)}%`, color: '#3B82F6' },
        { label: 'CPC', value: `$${(Math.sin(time * 0.5) * 0.5 + 2.5).toFixed(2)}`, color: '#F59E0B' },
      ];

      stats.forEach((stat, i) => {
        const x = canvas.width * 0.85;
        const y = canvas.height * 0.1 + i * 25;
        
        ctx.font = 'bold 12px Arial';
        ctx.fillStyle = stat.color;
        ctx.fillText(stat.label, x, y);
        
        ctx.font = '12px monospace';
        ctx.fillStyle = '#FFFFFF';
        ctx.fillText(stat.value, x + 50, y);
      });
    };

    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
      setIsHovering(true);
      
      // Update active metrics near cursor
      const metrics = particles
        .filter(p => p.type === 'metric' && 
          Math.abs(p.x - e.clientX) < 100 && 
          Math.abs(p.y - e.clientY) < 100)
        .map(p => p.text);
      
      setActiveMetrics(metrics.slice(0, 3));
    };

    const handleMouseLeave = () => {
      setIsHovering(false);
      setActiveMetrics([]);
    };

    const handleClick = () => {
      // Add metric burst on click
      for (let i = 0; i < 5; i++) {
        setTimeout(() => {
          const x = mousePos.x + (Math.random() - 0.5) * 100;
          const y = mousePos.y + (Math.random() - 0.5) * 100;
          const particle = new DigitalParticle('metric');
          particle.x = x;
          particle.y = y;
          particles.push(particle);
        }, i * 100);
      }
    };

    initParticles();
    animate();

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);
    canvas.addEventListener('click', handleClick);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      canvas.removeEventListener('click', handleClick);
    };
  }, [mousePos, isHovering, activeMetrics]);

  return (
    <>
      <canvas
        ref={canvasRef}
        className="fixed inset-0 pointer-events-auto cursor-pointer"
        style={{ 
          zIndex: 1,
          background: 'radial-gradient(circle at 20% 50%, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.98) 100%)'
        }}
      />
      
      {/* Active metrics display */}
      {activeMetrics.length > 0 && (
        <div className="fixed top-20 right-4 z-20 bg-gray-900/80 backdrop-blur-md rounded-lg p-3 border border-blue-500/30 shadow-lg">
          <div className="text-xs text-gray-400 mb-2">ACTIVE METRICS</div>
          <div className="space-y-1">
            {activeMetrics.map((metric, i) => (
              <div key={i} className="flex items-center space-x-2 text-white">
                <span className="text-lg">{metric}</span>
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default DigitalMarketingParticles;